import humanizeString from 'humanize-string';
import React, { useMemo } from 'react';
import {
  ActionTypes,
  EcommercePurchaseFactorField,
  ImpactMode,
  QualifioCampaignFactorField,
  QualifioLoyaltyBirthdayFactorField,
  QualifioLoyaltyMemberVisitFactorField,
  fixedEarningEventNames,
} from '@loyalty/shared-common/rule-api-client';
import { Box, Button, IconButton, MenuItem, SxProps, TextField } from '@mui/material';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { EventSource, Feature } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { QlfNumberField, colorPalette, sxs, useQlfTranslation } from '@qualifio/ui-commons';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash';
import { useTranslation } from 'react-i18next';
import { ArchivedCurrencyTooltip } from '../currency/components/archived-currency-tooltip';
import { CurrencyDropdown } from '../currency/components/currency-dropdown';
import { EarningRuleFormValue } from './form';
import { SxStyles } from '../shared/utils/types';
import { filterBinaryWebhooks } from '../program-incoming-webhooks/incoming-webhook-utils';
import { requiresReferral } from '../rules/utils';
import { useCurrencies } from '../currency/CurrenciesContext';
import { useFeatures } from '../features/FeaturesContext';
import { useIncomingWebhooks } from '../program-incoming-webhooks/IncomingWebhookContext';

interface Props {
  className?: string;
  sx?: SxProps;
}

const styles: SxStyles = {
  categoryField: {
    marginRight: 1,
    minWidth: 200,
    width: 'auto',
  },
  currencyAmountField: {
    width: 100,
  },
  currencyKindField: {
    display: 'flex',
    width: 150,
    marginLeft: 1,
  },
  word: {
    marginRight: 0.8,
    marginTop: '0.7em',
    color: colorPalette.grey.dark,
  },
  listItemContent: {
    padding: [1, 1],
    '&:last-child': {
      paddingBottom: 1,
    },
  },
};

function CurrencyEarningForm({ index }: { index: number }) {
  const { t } = useTranslation();
  const {
    watch,
    control,
    formState: { errors },
  } = useFormContext();
  const { features } = useFeatures();
  const { write: earningRuleWrite } = features[Feature.EARNING_RULE];
  const { webhooks } = useIncomingWebhooks();
  const binaryWebhooks = filterBinaryWebhooks(webhooks);

  const { currencies } = useCurrencies();

  const type = watch(`actions[${index}].impact.mode`);
  const eventSource = watch('eventSource');
  const eventName = watch('eventName');

  const binaryCounters: { [key: string]: { value: string; text: string }[] } = {};
  binaryWebhooks.forEach(webhook => {
    binaryCounters[`BINARY-webhook_${webhook.id}`] = (webhook.config?.counters || []).map(
      counter => ({
        value: counter,
        text: humanizeString(counter),
      }),
    );
  });

  const ruleCurrency = currencies.find(
    currency => currency.id === watch(`actions[${index}].impact.currency`),
  );

  const earningFactors: Record<string, Array<{ value: string; text: string }>> = {
    'QUALIFIO_ENGAGE-CAMPAIGN': [
      {
        value: QualifioCampaignFactorField.DISTANCE,
        text: t('earning-rules.earnings.qualifio_engage-campaign.distance'),
      },
      {
        value: QualifioCampaignFactorField.POINT,
        text: t('earning-rules.earnings.qualifio_engage-campaign.point'),
      },
      {
        value: QualifioCampaignFactorField.TIME,
        text: t('earning-rules.earnings.qualifio_engage-campaign.time'),
      },
    ],
    'QUALIFIO_LOYALTY-MEMBER_VISIT': [
      {
        value: QualifioLoyaltyMemberVisitFactorField.TOTAL_DAILY_VISITS,
        text: t('earning-rules.earnings.qualifio_loyalty-member_visit.totalDailyVisits'),
      },
      {
        value: QualifioLoyaltyMemberVisitFactorField.CONSECUTIVE_DAILY_VISITS,
        text: t('earning-rules.earnings.qualifio_loyalty-member_visit.consecutiveDailyVisits'),
      },
    ],
    'QUALIFIO_LOYALTY-BIRTHDAY': [
      {
        value: QualifioLoyaltyBirthdayFactorField.AGE,
        text: t('earning-rules.earnings.qualifio_loyalty-birthday.age'),
      },
    ],
    'ECOMMERCE-PURCHASE': [
      {
        value: EcommercePurchaseFactorField.AMOUNT_VAT_EXCLUDED,
        text: t('earning-rules.earnings.ecommerce-purchase.amountVatExcluded'),
      },
      {
        value: EcommercePurchaseFactorField.AMOUNT_VAT_INCLUDED,
        text: t('earning-rules.earnings.ecommerce-purchase.amountVatIncluded'),
      },
      {
        value: EcommercePurchaseFactorField.SHIPPING_COST_VAT_INCLUDED,
        text: t('earning-rules.earnings.ecommerce-purchase.shippingCostVatIncluded'),
      },
      {
        value: EcommercePurchaseFactorField.TOTAL_AMOUNT_VAT_INCLUDED,
        text: t('earning-rules.earnings.ecommerce-purchase.totalAmountVatIncluded'),
      },
      {
        value: EcommercePurchaseFactorField.BASKET_TOTAL,
        text: t('earning-rules.earnings.ecommerce-purchase.basketTotal'),
      },
      {
        value: EcommercePurchaseFactorField.QUANTITY_TOTAL,
        text: t('earning-rules.earnings.ecommerce-purchase.quantityTotal'),
      },
    ],
    'REFERRAL-ECOMMERCE': [
      {
        value: EcommercePurchaseFactorField.AMOUNT_VAT_EXCLUDED,
        text: t('earning-rules.earnings.ecommerce-purchase.amountVatExcluded'),
      },
      {
        value: EcommercePurchaseFactorField.AMOUNT_VAT_INCLUDED,
        text: t('earning-rules.earnings.ecommerce-purchase.amountVatIncluded'),
      },
      {
        value: EcommercePurchaseFactorField.SHIPPING_COST_VAT_INCLUDED,
        text: t('earning-rules.earnings.ecommerce-purchase.shippingCostVatIncluded'),
      },
      {
        value: EcommercePurchaseFactorField.TOTAL_AMOUNT_VAT_INCLUDED,
        text: t('earning-rules.earnings.ecommerce-purchase.totalAmountVatIncluded'),
      },
      {
        value: EcommercePurchaseFactorField.BASKET_TOTAL,
        text: t('earning-rules.earnings.ecommerce-purchase.basketTotal'),
      },
      {
        value: EcommercePurchaseFactorField.QUANTITY_TOTAL,
        text: t('earning-rules.earnings.ecommerce-purchase.quantityTotal'),
      },
    ],
    ...binaryCounters,
  };

  switch (type) {
    case ImpactMode.FIXED_AMOUNT:
      return (
        <>
          <Box component="p" sx={styles.word}>
            {t('earning-rules.earnings.words.of')}
          </Box>

          <Controller
            control={control}
            name={`actions[${index}].impact.value`}
            key={ImpactMode.FIXED_AMOUNT}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <QlfNumberField
                {...field}
                disabled={!earningRuleWrite}
                max={99999999999}
                error={!!error}
                helperText={error?.message}
                sx={styles.currencyAmountField}
                id="number-points-id"
              />
            )}
          />
          <Controller
            control={control}
            name={`actions[${index}].impact.currency`}
            render={({ field: { ref, ...field } }) => (
              <CurrencyDropdown
                {...field}
                sx={styles.currencyKindField}
                disabled={!earningRuleWrite}
              />
            )}
          />
          {ruleCurrency?.deletedAt ? <ArchivedCurrencyTooltip sx={styles.listItemContent} /> : null}
        </>
      );
    case ImpactMode.VARIABLE_AMOUNT:
      const selectedFactor = watch(`actions[${index}].impact.factorField`);
      addMissingDeprecatedFactorField(earningFactors, { eventSource, eventName }, selectedFactor);
      return (
        <>
          <Box component="p" sx={styles.word}>
            {t('earning-rules.earnings.words.from')}
          </Box>
          <Controller
            control={control}
            name={`actions[${index}].impact.factorField`}
            defaultValue={earningFactors[`${eventSource}-${eventName}`]?.[0]?.value || null}
            key={ImpactMode.VARIABLE_AMOUNT}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <TextField
                {...field}
                disabled={!earningRuleWrite}
                select
                error={!!error}
                helperText={error?.message}
                sx={styles.categoryField}
              >
                {earningFactors[`${eventSource}-${eventName}`]?.map(item => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.text}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Box sx={styles.word}>{t('earning-rules.earnings.words.and-multiply-by')}</Box>
          <Controller
            control={control}
            name={`actions[${index}].impact.factor`}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <QlfNumberField
                {...field}
                disabled={!earningRuleWrite}
                error={!!error}
                helperText={error?.message}
                max={99999999999}
                maxDecimals={4}
                sx={styles.currencyAmountField}
              />
            )}
          />
          <Controller
            control={control}
            name={`actions[${index}].impact.currency`}
            render={({ field: { ref, ...field } }) => (
              <CurrencyDropdown
                {...field}
                sx={styles.currencyKindField}
                disabled={!earningRuleWrite}
              />
            )}
          />
          {ruleCurrency?.deletedAt ? <ArchivedCurrencyTooltip sx={styles.listItemContent} /> : null}
        </>
      );
    default:
      return <></>;
  }
}

function addMissingDeprecatedFactorField(
  earningFactors: { [key: string]: { text: string; value: string }[] },
  event: { eventSource: string; eventName: string },
  selectedFactor?: string,
) {
  if (!selectedFactor) {
    return;
  }
  const { eventSource, eventName } = event;
  earningFactors[`${eventSource}-${eventName}`] =
    earningFactors[`${eventSource}-${eventName}`] || [];

  if (
    !earningFactors[`${eventSource}-${eventName}`].find(factor => factor.value === selectedFactor)
  ) {
    earningFactors[`${eventSource}-${eventName}`].push({
      value: selectedFactor,
      text: `${humanizeString(selectedFactor)} (\u{26A0} MISSING \u{26A0})`,
    });
  }
}

export function EarningRulePanelEarnings({ className, sx }: Props): React.ReactElement {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<EarningRuleFormValue>();
  const { currencies } = useCurrencies();
  const availableCurrencies = currencies.filter(currency => !currency.deletedAt);
  const { t } = useQlfTranslation('translation');
  const { features } = useFeatures();
  const { write: earningRuleWrite } = features[Feature.EARNING_RULE];
  const { webhooks } = useIncomingWebhooks();
  const binaryFixedWebhooks = filterBinaryWebhooks(webhooks).filter(
    webhook => !webhook.config?.jsonata?.includes('counters'),
  );
  const everyFixedEarningEventNames: string[] = Object.values(fixedEarningEventNames);

  binaryFixedWebhooks.forEach(webhook => {
    everyFixedEarningEventNames.push(`webhook_${webhook.id}`);
  });

  const eventSource = watch('eventSource');
  const eventName = watch('eventName');
  const requiresReferralCode = requiresReferral(watch('criteria'));

  const introductionText =
    eventSource === EventSource.REFERRAL
      ? t('earning-rules.properties.define-earnings.referrer')
      : requiresReferralCode
      ? t('earning-rules.properties.define-earnings.referee')
      : t('earning-rules.properties.define-earnings.general');

  const modesOptions = useMemo(
    () =>
      [
        { value: ImpactMode.FIXED_AMOUNT, text: t('earning-rules.earnings.words.fixed-amount') },
        !everyFixedEarningEventNames.includes(eventName)
          ? {
              value: ImpactMode.VARIABLE_AMOUNT,
              text: t('earning-rules.earnings.words.variable-amount'),
            }
          : undefined,
      ].filter((option): option is { value: ImpactMode; text: string } => !!option),
    [eventName],
  );

  const { fields, append, remove } = useFieldArray({
    control,
    name: `actions`,
  });

  const handleAdd = () => {
    append({
      kind: ActionTypes.TRANSACTION,
      impact: {
        mode: ImpactMode.FIXED_AMOUNT,
        value: null,
        currency: availableCurrencies[0]?.id || '',
      },
    });
  };

  const handleDelete = (index: number) => {
    remove(index);
  };

  return (
    <>
      <Box sx={{ color: colorPalette.grey.dark }}>
        {introductionText}
        {!!errors.actions && (
          <Box
            component={FontAwesomeIcon}
            sx={{ marginLeft: 0.5, color: colorPalette.red.main }}
            icon={faExclamationCircle}
          />
        )}
      </Box>

      <Box className={className} sx={sxs({ marginTop: 2 }, sx || {})}>
        {fields.map((action, index) => (
          <Box sx={{ display: 'flex', marginBottom: 1.5, alignItems: 'center' }} key={action.id}>
            <Box sx={{ display: 'flex' }}>
              <Controller
                control={control}
                name={`actions[${index}].impact.mode` as any}
                render={({ field: { ref, ...field } }) => (
                  <TextField
                    {...field}
                    select
                    sx={styles.categoryField}
                    disabled={!earningRuleWrite}
                    id={'multiple-choice-button-id'}
                  >
                    {modesOptions.map(item => (
                      <MenuItem key={item.value} value={item.value} id={`choice-${item.value}`}>
                        {item.text}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />

              <CurrencyEarningForm index={index} />
            </Box>
            {fields.length > 1 && (
              <IconButton
                disabled={!earningRuleWrite}
                sx={{
                  color: colorPalette.grey.main,
                  minWidth: 40,
                  marginLeft: 'auto',
                  '&:hover': {
                    backgroundColor: 'inherit',
                    color: colorPalette.turquoise.main,
                  },
                }}
                color="inherit"
                onClick={() => handleDelete(index)}
              >
                <FontAwesomeIcon size="sm" icon={faTrash} />
              </IconButton>
            )}
          </Box>
        ))}
      </Box>
      <Button
        disabled={!earningRuleWrite}
        sx={{ marginTop: 3 }}
        startIcon={<FontAwesomeIcon icon={faPlus} />}
        variant="contained"
        color="secondary"
        onClick={handleAdd}
      >
        {t('earning-rules.actions.add-earning')}
      </Button>
    </>
  );
}
